<template>
  <div class="mobile-page">
    <div class="sub-nav">
      <button class="back-btn" @click="back"><el-icon :size="18"><ArrowLeft /></el-icon></button>
      <span class="nav-title">巡检计划</span>
      <span class="nav-right"><button class="refresh-btn" @click="load"><el-icon :size="16"><Refresh /></el-icon></button></span>
    </div>

    <div class="mobile-body">
      <p class="publish-tip" v-if="publishedAt">最近发布：{{ publishedAt }}<template v-if="version"> · 版本 {{ shortVersion }}</template></p>

      <div class="chip-row">
        <span class="chip" :class="{ on: typeFilter === '' }" @click="typeFilter = ''">全部 ({{ list.length }})</span>
        <span v-for="t in types" :key="t" class="chip" :class="{ on: typeFilter === t }" @click="typeFilter = t">{{ t }} ({{ countOf(t) }})</span>
      </div>

      <div v-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="error" class="white-card center-box">
        <el-icon :size="36" color="#c5221f"><WarningFilled /></el-icon>
        <p class="sub-text">{{ error }}</p>
        <el-button size="small" type="primary" plain style="margin-top:10px" @click="load">重试</el-button>
      </div>
      <div v-else-if="list.length === 0" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Calendar /></el-icon>
        <p class="sub-text">暂无巡检计划</p>
      </div>

      <template v-else>
        <div class="plan-card" v-for="p in list" :key="p.id" @click="expandId = expandId === p.id ? '' : p.id">
          <div class="pc-head">
            <div class="pc-title">
              <span class="pc-name">{{ p.name || p.id }}</span>
              <span class="pill" :class="typePill(p.type)">{{ p.type || '日常' }}</span>
              <span v-if="isActive(p)" class="pill pill-green">进行中</span>
            </div>
            <el-icon class="pc-arrow" :class="{ open: expandId === p.id }"><ArrowDown /></el-icon>
          </div>
          <div class="pc-line">
            <el-icon :size="13"><Calendar /></el-icon>
            <span>{{ periodText(p) }}</span>
          </div>
          <div v-if="expandId === p.id" class="pc-detail">
            <div class="pc-row" v-if="p.category"><span class="pc-k">分类</span><span>{{ p.category }}</span></div>
            <div class="pc-row" v-if="p.period_type"><span class="pc-k">周期</span><span>{{ p.period_type }}</span></div>
            <div class="pc-row" v-if="p.days_of_week"><span class="pc-k">星期</span><span>{{ weekText(p.days_of_week) }}</span></div>
            <div class="pc-row" v-if="p.remark"><span class="pc-k">说明</span><span>{{ p.remark }}</span></div>
            <div class="pc-row" v-else><span class="pc-k">说明</span><span class="muted">—</span></div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { fetchSchedule } from '../api'
import dayjs from 'dayjs'

const router = useRouter()
function back() { router.back() }

const loading = ref(true)
const error = ref('')
const data = ref({ plans: [], duty: [], dutyLeaders: [] })
const expandId = ref('')
const typeFilter = ref('')

const publishedAt = computed(() => {
  const t = data.value.publishedAt
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : ''
})
const version = computed(() => data.value.version || '')
const shortVersion = computed(() => String(version.value).slice(-6))

const plans = computed(() => data.value.plans || [])
const types = computed(() => Array.from(new Set(plans.value.map(p => p.type || '日常').filter(Boolean))))
const list = computed(() => {
  if (!typeFilter.value) return plans.value
  return plans.value.filter(p => (p.type || '日常') === typeFilter.value)
})
function countOf(t) {
  return plans.value.filter(p => (p.type || '日常') === t).length
}
function typePill(t) {
  if (t === '日常') return 'pill-blue'
  if (t === '长假') return 'pill-purple'
  if (t === '节假日') return 'pill-orange'
  return 'pill-gray'
}

function periodText(p) {
  const s = p.start_date || ''
  const e = p.end_date || ''
  if (s && e) return `${s} 至 ${e}`
  if (s) return `${s} 起`
  if (e) return `至 ${e}`
  return '长期有效'
}

function isActive(p) {
  const today = dayjs().format('YYYY-MM-DD')
  const s = p.start_date
  const e = p.end_date
  if (s && today < s) return false
  if (e && today > e) return false
  return true
}

function weekText(d) {
  const map = { '1': '周一', '2': '周二', '3': '周三', '4': '周四', '5': '周五', '6': '周六', '7': '周日', '0': '周日' }
  return String(d).split(',').map(x => map[x.trim()] || x.trim()).join('、')
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const r = await fetchSchedule()
    data.value = r
  } catch (e) {
    error.value = e.message || '加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.publish-tip { font-size: 12px; color: #9aa0a6; margin: 4px 2px 8px; }

.plan-card {
  background: #fff; border-radius: 14px; padding: 14px; margin-bottom: 10px;
  box-shadow: 0 3px 12px rgba(31,45,61,.06); cursor: pointer; transition: transform .15s;
}
.plan-card:active { transform: scale(.99); }
.pc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; }
.pc-title { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.pc-name { font-size: 15.5px; font-weight: 700; color: #202124; }
.pc-arrow { color: #b0b3bb; transition: transform .2s; margin-top: 2px; }
.pc-arrow.open { transform: rotate(180deg); }

.pc-line { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #5f6368; margin-top: 8px; }
.pc-detail { border-top: 1px dashed #e8eaed; margin-top: 10px; padding-top: 8px; }
.pc-row { display: flex; gap: 10px; font-size: 13px; color: #3c4043; padding: 3px 0; line-height: 1.5; }
.pc-k { width: 42px; flex-shrink: 0; color: #9aa0a6; }
.muted { color: #b0b3bb; }
</style>
